import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Box,
  Chip,
  IconButton,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../api/client";

const STORAGE_KEY = "xpro_dismissed_future_contact_ids";

function loadDismissed() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(Number).filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function parseLooseDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  if (!s) return null;
  // aceita "YYYY-MM-DD HH:mm:ss" e variantes sem timezone
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

function formatWhen(dt, locale = "pt-BR") {
  if (!dt) return null;
  try {
    return dt.toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function formatRelativePast(ms, locale = "pt-BR") {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < 45_000) return rtf.format(-1, "second"); // "agora"/"há 1 segundo"
  if (abs < hour) return rtf.format(-Math.max(1, Math.round(abs / minute)), "minute");
  if (abs < day) return rtf.format(-Math.max(1, Math.round(abs / hour)), "hour");
  return rtf.format(-Math.max(1, Math.round(abs / day)), "day");
}

/**
 * Consulta solicitações em Contato futuro com data/hora já vencida e atribuídas ao usuário;
 * exibe diálogo para abrir o atendimento.
 */
export default function FutureContactDueReminder() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const dismissed = useRef(loadDismissed());
  const showingRef = useRef(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const poll = useCallback(async () => {
    if (showingRef.current) return;
    try {
      const res = await fetchData("/conversations/due-future-contact", "GET");
      const payload = res?.data;
      const list = Array.isArray(payload?.items) ? payload.items : [];
      const filtered = list.filter((row) => row?.id != null && !dismissed.current.has(Number(row.id)));
      if (filtered.length) {
        showingRef.current = true;
        setItems(filtered);
        setOpen(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60_000);
    return () => clearInterval(t);
  }, [poll]);

  const dismissMany = useCallback((rows) => {
    rows.forEach((x) => {
      if (x?.id != null) dismissed.current.add(Number(x.id));
    });
    saveDismissed(dismissed.current);
  }, []);

  const handleClose = useCallback(() => {
    dismissMany(items);
    showingRef.current = false;
    setOpen(false);
  }, [dismissMany, items]);

  const goToChat = useCallback((id) => {
    dismissMany([{ id }]);
    showingRef.current = false;
    setOpen(false);
    navigate("/chat", { state: { openConversationId: id } });
  }, [dismissMany, navigate]);

  const dismissOne = useCallback((id) => {
    dismissMany([{ id }]);
    setItems((prev) => {
      const next = prev.filter((x) => Number(x?.id) !== Number(id));
      if (!next.length) {
        showingRef.current = false;
        setOpen(false);
      }
      return next;
    });
  }, [dismissMany]);

  const sortedItems = useMemo(() => {
    const clone = Array.isArray(items) ? [...items] : [];
    const toTs = (row) => parseLooseDate(row?.nextContactAt)?.getTime() ?? Number.POSITIVE_INFINITY;
    clone.sort((a, b) => toTs(a) - toTs(b));
    return clone;
  }, [items]);

  const firstId = sortedItems?.[0]?.id;

  const dialogPaperSx = {
    borderRadius: 3,
    overflow: "hidden",
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 24px 70px ${alpha("#000", 0.55)}`
        : `0 24px 70px ${alpha("#000", 0.2)}`,
  };

  const headerSx = {
    pb: 1.5,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.background.paper, 0)})`
        : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.background.paper, 0)})`,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
      aria-labelledby="future-contact-due-title"
    >
      <DialogTitle id="future-contact-due-title" sx={headerSx}>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12),
              color: theme.palette.primary.main,
              flex: "0 0 auto",
              mt: 0.25,
            }}
          >
            <NotificationsActiveOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.2 }} noWrap>
                Contato futuro vencido
              </Typography>
              <Chip
                size="small"
                label={`${sortedItems.length}`}
                sx={{
                  fontWeight: 700,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.main, 0.18)
                      : alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.35 }}>
              Já passou do horário de retorno nas solicitações abaixo. Clique em uma para abrir direto no chat.
            </Typography>
          </Box>

          <Tooltip title="Fechar e dispensar desta sessão">
            <IconButton onClick={handleClose} size="small" aria-label="Fechar">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 1.5,
          pb: 0,
          // Uma única rolagem: o conteúdo do Dialog.
          maxHeight: { xs: "72vh", sm: "70vh" },
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.55 : 0.9)}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <List
            dense
            disablePadding
            sx={{
              "& .MuiListItemButton-root": {
                py: 1.1,
                px: 1.25,
                alignItems: "stretch",
                gap: 1,
              },
            }}
          >
            {sortedItems.map((row, idx) => {
              const dt = parseLooseDate(row?.nextContactAt);
              const ts = dt?.getTime?.();
              const rel = typeof ts === "number" ? formatRelativePast(Date.now() - ts) : null;
              const abs = dt ? formatWhen(dt) : null;
              const client = row?.clientName || "Cliente";
              const id = row?.id;

              return (
                <React.Fragment key={id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => goToChat(id)}
                      sx={{
                        "&:hover .goIcon": { transform: "translateX(2px)", opacity: 1 },
                        "&:focus-visible": {
                          outline: `3px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap title={client}>
                            {client}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: "text.secondary",
                              bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.06),
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 999,
                              flex: "0 0 auto",
                            }}
                          >
                            #{id}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.35, minWidth: 0 }}>
                          {abs ? (
                            <Typography variant="body2" color="text.secondary" noWrap title={abs}>
                              {abs}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              Horário não informado
                            </Typography>
                          )}

                          {rel ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.warning.main,
                                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.16 : 0.12),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 999,
                                flex: "0 0 auto",
                              }}
                              title={abs || ""}
                            >
                              {rel}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>

                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: "0 0 auto" }}>
                        <Tooltip title="Dispensar esta solicitação">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              dismissOne(id);
                            }}
                            aria-label={`Dispensar solicitação ${id}`}
                          >
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <ArrowForwardRoundedIcon
                          className="goIcon"
                          fontSize="small"
                          style={{ opacity: 0.65, transition: "transform 120ms ease, opacity 120ms ease" }}
                        />
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                  {idx < sortedItems.length - 1 ? <Divider component="li" /> : null}
                </React.Fragment>
              );
            })}

            {!sortedItems.length ? (
              <Box sx={{ p: 2.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Tudo certo por aqui
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Não há solicitações de “Contato futuro” vencidas no momento.
                </Typography>
              </Box>
            ) : null}
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" variant="text">
          Dispensar todos
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => firstId != null && goToChat(firstId)}
          variant="contained"
          disableElevation
          disabled={firstId == null}
          startIcon={<ArrowForwardRoundedIcon />}
        >
          Abrir primeiro
        </Button>
      </DialogActions>
    </Dialog>
  );
}
